// frontend/src/pages/Jobs.js
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { AuthContext } from "../contexts/AuthContext";
import {
  apiFetch,
  post as apiPost,
  put as apiPut,
  delete as apiDelete,
  fetchOrganizations,
  getSelectedOrgId,
  setSelectedOrgId,
} from "../services/api";

const PAGE_SIZES = [10, 20, 50];

// Status mapping required:
// 0 → Cancelled, 1 → Open, 2 → On Hold, 3 → Closed
const STATUS_OPTIONS = [
  { value: 1, label: "Open" },
  { value: 2, label: "On Hold" },
  { value: 0, label: "Cancelled" },
  { value: 3, label: "Closed" },
];
const statusLabel = (n) => {
  const m = { 0: "Cancelled", 1: "Open", 2: "On Hold", 3: "Closed" };
  return m[Number(n)] ?? "-";
};

// Non-active statuses (for row styling)
const NON_ACTIVE_STATUSES = new Set([0, 2, 3]); // Cancelled, On Hold, Closed

const DIFFICULTY_OPTIONS = [
  { value: 0, label: "Easy" },
  { value: 1, label: "Intermediate" },
  { value: 2, label: "Difficult" },
];
const DETAILS_OPTIONS = [
  { value: 0, label: "Basic" },
  { value: 1, label: "Intermediate" },
  { value: 2, label: "Detailed" },
];
const difficultyLabel = (n) => {
  const m = { 0: "Easy", 1: "Intermediate", 2: "Difficult" };
  return m[Number(n)] ?? "-";
};
const detailsLabel = (n) => {
  const m = { 0: "Basic", 1: "Intermediate", 2: "Detailed" };
  return m[Number(n)] ?? "-";
};

// NEW: Evaluation Harshness options/labels
const HARSHNESS_OPTIONS = [
  { value: 0, label: "Lenient (Benefit of Doubt)" },
  { value: 1, label: "Moderate (Fair and Balanced)" },
  { value: 2, label: "Harsh (No Mercy)" },
];
const harshnessLabel = (n) => {
  const m = {
    0: "Lenient",
    1: "Moderate",
    2: "Harsh",
  };
  return m[Number(n)] ?? "-";
};

export default function Jobs() {
  const { user } = useContext(AuthContext);
  const isSuper =
    String(user?.group_role || user?.role || user?.user_group_name || "")
      .toLowerCase() === "superadmin";
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState(getSelectedOrgId()); // null => All
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  // Sort state
  const [sort, setSort] = useState({ key: "id", dir: "desc" });
  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "id" ? "desc" : "asc" }
    );
  };
  const sortArrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  // Excel-like filters
  const [excelFilters, setExcelFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const headerAnchorRefs = useRef({});

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    job_req_title: "",
    job_req_description: "",
    job_req_summary: "",                            // NEW
    job_req_status: 1, // 1 Open, 2 On Hold, 0 Cancelled, 3 Closed
    organization_id: null, // superadmin only
    job_area_of_interest_id: "", // option.id (string)
    job_location_id: "",          // option.id (string)
    job_req_ai_interview_difficulty_level: 1,
    job_req_ai_interview_details_level: 1,
    job_req_ai_evaluation_harshness: 1,            // NEW
  });

  // -------- Lookups --------
  const [areasLookup, setAreasLookup] = useState([]);       // [{id, realId, descr}]
  const [locationsLookup, setLocationsLookup] = useState([]); // [{id, realId, descr, label, countryId?}]

  // -------- AI Assistant Modal --------
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalLanguages, setAiModalLanguages] = useState([]);
  const [aiModalForm, setAiModalForm] = useState({
    job_title: "",
    job_info: "",
    language: "",
    format: "html",
  });
  const [aiGenerating, setAiGenerating] = useState(false);

  // -------- Description View Mode --------
  const [descriptionViewMode, setDescriptionViewMode] = useState("edit"); // "edit" or "preview"

  // ---------- Orgs ----------
  useEffect(() => {
    if (!isSuper) return;
    fetchOrganizations().then((d) => setOrgs(Array.isArray(d) ? d : [])).catch(() => setOrgs([]));
  }, [isSuper]);

  const orgNameById = useMemo(() => {
    const m = new Map();
    (orgs || []).forEach((o) => m.set(o.id ?? o.organization_id, o.name ?? o.organization_name));
    return m;
  }, [orgs]);

  const handleOrgChange = (e) => {
    const v = e.target.value;
    const next = v === "" ? null : Number(v);
    setOrgFilter(next);
    setSelectedOrgId(next);
    setPage(1);
  };

  // ---------- Helpers ----------
  const removeWithConfirm = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    await remove(id); // uses your existing remove()
  };

  const hideJob = async (id) => {
    if (!window.confirm("Are you sure you want to hide this job? Once hidden, it will not be shown again here!")) return;
    try {
      await apiPut(`/api/jobs/${id}`, { job_req_hide: 1 });
      setJobs((list) => (list || []).filter((j) => (j.job_req_id ?? j.id) !== id));
    } catch (e) {
      console.error("Hide job failed:", e);
      alert("Failed to hide job.");
    }
  };

  const areaOfInterestText = (row) =>
    row.job_area_of_interest_descr
      ?? row.JOB_AREA_OF_INTEREST?.job_area_of_interest_descr
      ?? row.job_area_of_interest?.job_area_of_interest_descr
      ?? "-";

  const locationText = (row) => {
    const city =
      row.job_location_descr
      ?? row.JOB_LOCATION?.job_location_descr
      ?? row.job_location?.job_location_descr
      ?? null;
    const country =
      row.country_name
      ?? row.COUNTRY?.country_name
      ?? row.job_location?.country?.country_name
      ?? null;
    return [city, country].filter(Boolean).join(", ") || "-";
  };

  // Stable synthetic ids when no backend ID (fallback only)
  const synthId = (s) => {
    if (!s) return "";
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return `name:${h}`;
  };

  // Fallback derived options from jobs (if lookups fail)
  const derivedAreas = useMemo(() => {
    const map = new Map();
    for (const j of jobs || []) {
      const realId =
        j.job_area_of_interest_id ??
        j.JOB_AREA_OF_INTEREST?.job_area_of_interest_id ??
        null;
      const descr = areaOfInterestText(j);
      if (!descr || descr === "-") continue;
      const id = realId != null ? String(realId) : synthId(descr);
      if (!map.has(id)) map.set(id, { id, realId: realId ?? null, descr });
    }
    return Array.from(map.values()).sort((a, b) => a.descr.localeCompare(b.descr));
  }, [jobs]);

  const derivedLocations = useMemo(() => {
    const map = new Map();
    for (const j of jobs || []) {
      const realId =
        j.job_location_id ??
        j.JOB_LOCATION?.job_location_id ??
        null;
      const city =
        j.job_location_descr ??
        j.JOB_LOCATION?.job_location_descr ??
        j.job_location?.job_location_descr ??
        null;
      const countryName =
        j.country_name ??
        j.COUNTRY?.country_name ??
        j.job_location?.country?.country_name ??
        "";
      if (!city) continue;

      const id = realId != null ? String(realId) : synthId(`${city}|||${countryName}`);
      const label = [city, countryName].filter(Boolean).join(", ");
      if (!map.has(id)) map.set(id, { id, realId: realId ?? null, descr: city, label });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [jobs]);

  // Helpers to try multiple endpoints without breaking anything
  async function tryFetchList(urls) {
    for (const u of urls) {
      try {
        const v = await apiFetch(u);
        if (Array.isArray(v) && v.length) return v;
      } catch {}
    }
    for (const u of urls) {
      try {
        const v = await apiFetch(u);
        if (Array.isArray(v)) return v;
      } catch {}
    }
    return [];
  }

  async function loadLookups() {
    try {
      const combined = await apiFetch(`/api/jobs/lookups`);
      if (combined && typeof combined === "object") {
        const a = Array.isArray(combined.job_areas) ? combined.job_areas : [];
        const l = Array.isArray(combined.job_locations) ? combined.job_locations : [];
        if (a.length || l.length) {
          setAreasLookup(
            a.map((x) => ({
              id: String(x.job_area_of_interest_id ?? x.id),
              realId: Number(x.job_area_of_interest_id ?? x.id),
              descr: String(x.job_area_of_interest_descr ?? x.descr ?? x.name ?? ""),
            }))
            .filter((x) => x.realId && x.descr)
            .sort((aa, bb) => aa.descr.localeCompare(bb.descr))
          );
          setLocationsLookup(
            l.map((x) => {
              const realId = Number(x.job_location_id ?? x.id);
              const city = String(x.job_location_descr ?? x.descr ?? x.name ?? "");
              const country = String(x.country_name ?? x.country ?? "");
              return {
                id: String(realId || synthId(`${city}|||${country}`)),
                realId: realId || null,
                descr: city,
                label: [city, country].filter(Boolean).join(", "),
                countryId: x.country_id ?? x.countryId ?? null,
              };
            })
            .filter((x) => x.descr)
            .sort((aa, bb) => aa.label.localeCompare(bb.label))
          );
          return; // success
        }
      }
    } catch {}

    const [areas, locations] = await Promise.all([
      tryFetchList([`/api/jobs/lookups/areas`, `/api/job_area_of_interest`]),
      tryFetchList([`/api/jobs/lookups/locations`, `/api/job_locations`]),
    ]);

    const normalizedAreas = areas
      .map((x) => ({
        id: String(x.job_area_of_interest_id ?? x.id),
        realId: Number(x.job_area_of_interest_id ?? x.id),
        descr: String(x.job_area_of_interest_descr ?? x.descr ?? x.name ?? ""),
      }))
      .filter((x) => x.realId && x.descr)
      .sort((a, b) => a.descr.localeCompare(b.descr));

    const normalizedLocs = locations
      .map((x) => {
        const realId = Number(x.job_location_id ?? x.id);
        const city = String(x.job_location_descr ?? x.descr ?? x.name ?? "");
        const country = String(x.country_name ?? x.country ?? "");
        return {
          id: String(realId || synthId(`${city}|||${country}`)),
          realId: realId || null,
          descr: city,
          label: [city, country].filter(Boolean).join(", "),
          countryId: x.country_id ?? x.countryId ?? null,
        };
      })
      .filter((x) => x.descr)
      .sort((a, b) => a.label.localeCompare(b.label));

    setAreasLookup(normalizedAreas);
    setLocationsLookup(normalizedLocs);
  }

  // ---------- Jobs fetch ----------
  async function fetchScopedList(basePath) {
    if (!isSuper) {
      return (await apiFetch(basePath)) ?? [];
    }
    if (orgFilter == null) {
      return (await apiFetch(`${basePath}?all=1`).catch(() => [])) ?? [];
    }
    let data = await apiFetch(`${basePath}?orgId=${orgFilter}`).catch(() => null);
    if (Array.isArray(data) && data.length) return data;

    data = await apiFetch(`${basePath}?organizationId=${orgFilter}`).catch(() => null);
    if (Array.isArray(data) && data.length) return data;

    data = await apiFetch(`${basePath}?organization_id=${orgFilter}`).catch(() => null);
    if (Array.isArray(data) && data.length) return data;

    data = await apiFetch(`${basePath}?all=1`).catch(() => []);
    const orgKey = (row) =>
      row.organization_id ?? row.org_id ?? row.orgId ?? row.organizationId ?? null;
    return (Array.isArray(data) ? data : []).filter((row) => Number(orgKey(row)) === Number(orgFilter));
  }

  const loadJobs = () => {
    setLoading(true);
    fetchScopedList(`/api/jobs`)
      .then((rows) => setJobs(Array.isArray(rows) ? rows : []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadLookups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadJobs(); }, [isSuper, orgFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- AI Modal Functions ----------
  const openAiModal = async () => {
    // Load languages for the modal
    try {
      const langs = await apiFetch(`/api/jobs/ai-languages`);
      setAiModalLanguages(Array.isArray(langs) ? langs : []);
    } catch (err) {
      console.error("Failed to load AI languages:", err);
      setAiModalLanguages([]);
    }
    // Pre-fill job title from form
    setAiModalForm({
      job_title: form.job_req_title || "",
      job_info: "",
      language: "",
      format: "html",
    });
    setAiModalOpen(true);
  };

  const closeAiModal = () => {
    setAiModalOpen(false);
    setAiGenerating(false);
  };

  const handleAiGenerate = async () => {
    // Validate modal form
    if (!aiModalForm.job_title || aiModalForm.job_title.length < 5) {
      return alert("Job Title must be at least 5 characters.");
    }
    if (aiModalForm.job_title.length > 128) {
      return alert("Job Title must be at most 128 characters.");
    }
    if (!aiModalForm.job_info || aiModalForm.job_info.length < 15) {
      return alert("Information About The Job must be at least 15 characters.");
    }
    if (aiModalForm.job_info.length > 32768) {
      return alert("Information About The Job must be at most 32,768 characters.");
    }
    if (!aiModalForm.language) {
      return alert("Please select a Job Summary & Description Language.");
    }
    if (!aiModalForm.format) {
      return alert("Please select a Job Description Format.");
    }

    setAiGenerating(true);
    try {
      const result = await apiPost(`/api/jobs/generate-description`, {
        job_title: aiModalForm.job_title,
        job_info: aiModalForm.job_info,
        language: aiModalForm.language,
        format: aiModalForm.format,
      });

      // Validate response has expected fields
      if (!result || typeof result !== 'object') {
        throw new Error("Invalid response received from AI service. Please try again.");
      }

      // Check if we have the expected fields (job_summary and job_descr)
      if (!result.job_summary && !result.job_descr) {
        throw new Error("AI service returned an incomplete response. Please try again.");
      }

      // Update the main form with generated content
      setForm((prev) => ({
        ...prev,
        job_req_summary: result.job_summary || prev.job_req_summary,
        job_req_description: result.job_descr || prev.job_req_description,
      }));
      closeAiModal();
    } catch (err) {
      console.error("AI generation failed:", err);
      // Show human-friendly error message
      const errorMessage = err?.message || "Failed to generate job description. Please try again later.";
      alert(errorMessage);
    } finally {
      setAiGenerating(false);
    }
  };

  // ---------- Column getters for sorting/filtering ----------
  const colGetters = useMemo(
    () => ({
      id: (r) => Number(r.job_req_id ?? r.id ?? 0),
      title: (r) => (r.job_req_title ?? r.title ?? "").toString(),
      area: (r) => areaOfInterestText(r),
      location: (r) => locationText(r),
      difficulty: (r) => difficultyLabel(r.job_req_ai_interview_difficulty_level),
      details: (r) => detailsLabel(r.job_req_ai_interview_details_level),
      harshness: (r) => harshnessLabel(r.job_req_ai_evaluation_harshness),
      status: (r) => statusLabel(Number(r.job_req_status ?? r.status ?? 0)),
      org: (r) =>
        orgNameById.get(r.organization_id) ??
        (r.organization_id != null ? `#${r.organization_id}` : ""),
    }),
    [orgNameById]
  );

  const getFilterValue = useCallback(
    (key, r) => {
      const getter = colGetters[key];
      const v = getter ? getter(r) : "";
      return typeof v === "number" ? v : (v ?? "").toString();
    },
    [colGetters]
  );

  const uniqueValuesForKey = useCallback(
    (key) => {
      const set = new Set();
      for (const r of jobs || []) set.add(getFilterValue(key, r));
      const arr = Array.from(set.values());
      return arr.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    },
    [jobs, getFilterValue]
  );

  // Filtered rows based on excel filters
  const filteredRows = useMemo(() => {
    const activeKeys = Object.keys(excelFilters).filter(
      (k) => excelFilters[k] && excelFilters[k] instanceof Set
    );
    if (activeKeys.length === 0) return jobs || [];
    return (jobs || []).filter((r) =>
      activeKeys.every((k) => {
        const selectedSet = excelFilters[k];
        const cellVal = getFilterValue(k, r);
        return selectedSet.has(cellVal);
      })
    );
  }, [jobs, excelFilters, getFilterValue]);

  // Sorted rows
  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    const getter = colGetters[sort.key];
    if (!getter) return arr;
    arr.sort((a, b) => {
      const av = getter(a);
      const bv = getter(b);
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredRows, sort, colGetters]);

  // ---------- Form handlers ----------
  const startCreate = () => {
    setEditingId(null);
    setForm({
      job_req_title: "",
      job_req_description: "",
      job_req_summary: "",                            // NEW
      job_req_status: 1,
      organization_id: isSuper ? (orgFilter ?? "") : null,
      job_area_of_interest_id: "",
      job_location_id: "",
      job_req_ai_interview_difficulty_level: 1,
      job_req_ai_interview_details_level: 1,
      job_req_ai_evaluation_harshness: 1,            // NEW
    });
  };

  const startEdit = (row) => {
    const id = row.job_req_id ?? row.id;

    // Resolve AOI against lookups (fallback to derived lists)
    const allAreas = areasLookup.length ? areasLookup : derivedAreas;
    const aoiRealId = row.job_area_of_interest_id ?? row.JOB_AREA_OF_INTEREST?.job_area_of_interest_id ?? null;
    const aoiDescr = areaOfInterestText(row);
    let aoiOption = null;
    if (aoiRealId != null) {
      aoiOption = allAreas.find((a) => String(a.realId) === String(aoiRealId)) || null;
    }
    if (!aoiOption && aoiDescr) {
      aoiOption = allAreas.find((a) => a.descr === aoiDescr) || null;
    }

    // Resolve Location against lookups (fallback to derived lists)
    const allLocs = locationsLookup.length ? locationsLookup : derivedLocations;
    const locRealId = row.job_location_id ?? row.JOB_LOCATION?.job_location_id ?? null;
    const cityOnly =
      row.job_location_descr ??
      row.JOB_LOCATION?.job_location_descr ??
      row.job_location?.job_location_descr ??
      null;
    let locOption = null;
    if (locRealId != null) {
      locOption = allLocs.find((l) => String(l.realId) === String(locRealId)) || null;
    }
    if (!locOption && cityOnly) {
      locOption = allLocs.find((l) => l.descr === cityOnly) || null;
    }

    setEditingId(id);
    setForm({
      job_req_title: row.job_req_title ?? row.title ?? "",
      job_req_description: row.job_req_description ?? row.description ?? "",
      job_req_summary: row.job_req_summary ?? "",                                // NEW
      job_req_status: Number(row.job_req_status ?? row.status ?? 1),
      organization_id: row.organization_id ?? (isSuper ? (orgFilter ?? "") : null),
      job_area_of_interest_id: aoiOption?.id ?? "",
      job_location_id: locOption?.id ?? "",
      job_req_ai_interview_difficulty_level: Number(row.job_req_ai_interview_difficulty_level ?? 1),
      job_req_ai_interview_details_level: Number(row.job_req_ai_interview_details_level ?? 1),
      job_req_ai_evaluation_harshness: Number(row.job_req_ai_evaluation_harshness ?? 1), // NEW
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => startCreate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ==================== REQUIRED FIELD VALIDATIONS ====================

    // Organization (for superadmin)
    if (isSuper && !form.organization_id) {
      return alert("Organization needs to be selected!");
    }

    // Job Title
    const titleLen = (form.job_req_title || "").length;
    if (!form.job_req_title || titleLen === 0) {
      return alert("Title needs to be entered!");
    }
    if (titleLen < 5) {
      return alert("Job Title must be at least 5 characters.");
    }
    if (titleLen > 128) {
      return alert("Job Title must be at most 128 characters.");
    }

    // Status
    if (form.job_req_status === "" || form.job_req_status == null) {
      return alert("Status needs to be selected!");
    }

    // Area of Interest
    if (!form.job_area_of_interest_id) {
      return alert("Area of Interest needs to be selected!");
    }

    // Location
    if (!form.job_location_id) {
      return alert("Location needs to be selected!");
    }

    // AI Interview Difficulty Level
    if (form.job_req_ai_interview_difficulty_level === "" || form.job_req_ai_interview_difficulty_level == null) {
      return alert("AI Interview Difficulty Level needs to be selected!");
    }

    // AI Interview Details Level
    if (form.job_req_ai_interview_details_level === "" || form.job_req_ai_interview_details_level == null) {
      return alert("AI Interview Details Level needs to be selected!");
    }

    // Evaluation Harshness
    if (form.job_req_ai_evaluation_harshness === "" || form.job_req_ai_evaluation_harshness == null) {
      return alert("Evaluation Harshness needs to be selected!");
    }

    // ==================== JOB SUMMARY VALIDATION ====================
    const summaryLen = (form.job_req_summary || "").length;
    if (!form.job_req_summary || summaryLen === 0) {
      return alert("Job Summary needs to be entered!");
    }
    if (summaryLen > 350) {
      return alert("Job Summary should be between 250 and 350 characters");
    }
    if (summaryLen < 20) {
      return alert("Job Summary is too short. It should be between 250 and 350 characters");
    }
    if (summaryLen >= 20 && summaryLen < 250) {
      const confirmed = window.confirm(
        "The Job Summary ideal length is 250 to 350 characters, a shorter summary may not convey the job requirement effectively!"
      );
      if (!confirmed) return;
    }

    // ==================== JOB DESCRIPTION VALIDATION ====================
    const descLen = (form.job_req_description || "").length;
    if (!form.job_req_description || descLen === 0) {
      return alert("Description needs to be entered!");
    }
    if (descLen > 32768) {
      return alert(
        `Job Descriptions best practices is to stay between 8,000-10,000 characters. You have entered ${descLen.toLocaleString()} characters`
      );
    }
    if (descLen < 300) {
      return alert("Job Description is too short. Ideal job description is between 3,000 and 20,000 characters");
    }
    if (descLen >= 300 && descLen < 3000) {
      const confirmed = window.confirm(
        "The Job Description ideal length is between 3,000 and 20,000 characters, a shorter job description may not convey the job requirement effectively!"
      );
      if (!confirmed) return;
    }

    // ==================== LOOKUP VALIDATIONS ====================
    const allAreas = areasLookup.length ? areasLookup : derivedAreas;
    const allLocs  = locationsLookup.length ? locationsLookup : derivedLocations;

    const chosenArea = allAreas.find((a) => String(a.id) === String(form.job_area_of_interest_id)) || null;
    const chosenLoc  = allLocs.find((l) => String(l.id) === String(form.job_location_id)) || null;

    if (!chosenLoc) {
      return alert("Please select a valid Location.");
    }
    if (chosenLoc.realId == null) {
      return alert("This Location does not have a backend ID. Please select an existing Location from JOB_LOCATION.");
    }

    // ==================== BUILD PAYLOAD ====================
    const payload = {
      job_req_title: form.job_req_title,
      job_req_description: form.job_req_description,
      job_req_summary: String(form.job_req_summary ?? "").slice(0, 350),
      job_req_status: Number(form.job_req_status),
      job_location_id: Number(chosenLoc.realId),
      job_req_ai_interview_difficulty_level: Number(form.job_req_ai_interview_difficulty_level ?? 1),
      job_req_ai_interview_details_level: Number(form.job_req_ai_interview_details_level ?? 1),
      job_req_ai_evaluation_harshness: Number(form.job_req_ai_evaluation_harshness ?? 1),
      ...(isSuper && form.organization_id != null && form.organization_id !== ""
        ? { organization_id: Number(form.organization_id) }
        : {}),
    };

    if (chosenArea?.realId != null) {
      payload.job_area_of_interest_id = Number(chosenArea.realId);
    }

    // ==================== SUBMIT ====================
    try {
      if (editingId) {
        const saved = await apiPut(`/api/jobs/${editingId}`, payload);
        setJobs((list) =>
          (list || []).map((j) =>
            (j.job_req_id ?? j.id) === (saved.job_req_id ?? saved.id) ? saved : j
          )
        );
      } else {
        const created = await apiPost(`/api/jobs`, payload);
        setJobs((list) => [created, ...(list || [])]);
      }
      cancelEdit();
    } catch (err) {
      console.error("Save job failed:", err);
      alert("Failed to save job.");
    }
  };

  const remove = async (id) => {
    try {
      await apiDelete(`/api/jobs/${id}`);
      setJobs((list) => (list || []).filter((j) => (j.job_req_id ?? j.id) !== id));
    } catch (err) {
      console.error("Delete job failed:", err);
    }
  };

  // ---------- Inline status change in table ----------
  async function changeRowStatus(jobId, newVal) {
    try {
      const saved = await apiPut(`/api/jobs/${jobId}`, { job_req_status: Number(newVal) });
      setJobs((list) =>
        (list || []).map((j) =>
          (j.job_req_id ?? j.id) === (saved.job_req_id ?? saved.id)
            ? { ...j, ...saved }
            : j
        )
      );
    } catch (e) {
      console.error("Status update failed:", e);
      alert("Failed to update status.");
    }
  }

  // Derived pagination (using sortedRows instead of jobs)
  const total = sortedRows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const pageRows = sortedRows.slice(startIdx, startIdx + pageSize);

  const gotoApplicants = (jobId) => {
    navigate(`/jobs/${jobId}/applicants${isSuper ? (orgFilter != null ? `?orgId=${orgFilter}` : `?all=1`) : ""}`);
  };

  // Which lists to render in dropdowns
  const areasToRender = areasLookup.length ? areasLookup : derivedAreas;
  const locationsToRender = locationsLookup.length ? locationsLookup : derivedLocations;

  // Header cell with filter icon
  const headerCell = (label, key) => {
    if (!headerAnchorRefs.current[key]) headerAnchorRefs.current[key] = { current: null };
    const values = uniqueValuesForKey(key);
    const selected = excelFilters[key] ?? new Set(values);
    const active = selected.size !== values.length;
    return (
      <th
        className="p-2 border-b cursor-pointer select-none relative"
        onClick={() => toggleSort(key)}
      >
        <div className="flex items-center">
          <span>
            {label}
            {sortArrow(key)}
          </span>
          <span
            className="ml-2 px-1 rounded hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              setOpenFilterKey((prev) => (prev === key ? null : key));
            }}
            ref={(el) => (headerAnchorRefs.current[key] = { current: el })}
            title="Filter"
          >
            <FilterIcon active={active} />
          </span>
        </div>
        <FilterDropdown
          open={openFilterKey === key}
          onClose={() => setOpenFilterKey(null)}
          anchorRef={headerAnchorRefs.current[key]}
          values={values}
          selectedSet={selected}
          onChange={(setVal) =>
            setExcelFilters((prev) => ({ ...prev, [key]: setVal }))
          }
        />
      </th>
    );
  };

  const clearAllFilters = () => {
    setExcelFilters({});
    setOpenFilterKey(null);
  };

  return (
    <div
      onClick={() => {
        if (openFilterKey) setOpenFilterKey(null);
      }}
    >
      <Navbar />
      <div className="p-8">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="text-2xl font-bold">Jobs</h1>
          <div className="ml-auto flex items-center gap-2">
            {isSuper && (
              <>
                <label className="text-sm text-gray-500">Organization:</label>
                <select className="border p-2 rounded" value={orgFilter ?? ""} onChange={handleOrgChange}>
                  <option value="">All</option>
                  {(orgs || []).map((o) => (
                    <option key={o.id ?? o.organization_id} value={o.id ?? o.organization_id}>
                      {o.name ?? o.organization_name}
                    </option>
                  ))}
                </select>
              </>
            )}
            <button className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700" onClick={startCreate}>
              + New Job
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mb-6 grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div className="flex flex-col lg:col-span-2">
            <label className="text-xs text-gray-600 mb-1">Title</label>
            <input
              className="border p-2 rounded"
              value={form.job_req_title}
              onChange={(e) => setForm({ ...form, job_req_title: e.target.value })}
              placeholder="Job title"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Status</label>
            <select
              className="border p-2 rounded"
              value={form.job_req_status}
              onChange={(e) => setForm({ ...form, job_req_status: Number(e.target.value) })}
            >
              <option value={1}>Open</option>
              <option value={2}>On Hold</option>
              <option value={0}>Cancelled</option>
              <option value={3}>Closed</option>
            </select>
          </div>

          {isSuper && (
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Organization</label>
              <select
                className="border p-2 rounded"
                value={form.organization_id ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    organization_id: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              >
                <option value="">Select Organization</option>
                {(orgs || []).map((o) => (
                  <option key={o.id ?? o.organization_id} value={o.id ?? o.organization_id}>
                    {o.name ?? o.organization_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Area of Interest (optional) */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Area of Interest</label>
            <select
              className="border p-2 rounded"
              value={form.job_area_of_interest_id}
              onChange={(e) => setForm({ ...form, job_area_of_interest_id: e.target.value })}
            >
              <option value="">Select Area</option>
              {(areasToRender || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.descr}
                </option>
              ))}
            </select>
          </div>

          {/* Location (required) */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Location</label>
            <select
              className="border p-2 rounded"
              value={form.job_location_id}
              onChange={(e) => setForm({ ...form, job_location_id: e.target.value })}
              required
            >
              <option value="">Select Location</option>
              {(locationsToRender || []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>

          {/* AI Interview Difficulty Level */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">AI Interview Difficulty Level</label>
            <select
              className="border p-2 rounded"
              value={form.job_req_ai_interview_difficulty_level}
              onChange={(e) => setForm({ ...form, job_req_ai_interview_difficulty_level: Number(e.target.value) })}
            >
              {DIFFICULTY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* AI Interview Details Level */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">AI Interview Details Level</label>
            <select
              className="border p-2 rounded"
              value={form.job_req_ai_interview_details_level}
              onChange={(e) => setForm({ ...form, job_req_ai_interview_details_level: Number(e.target.value) })}
            >
              {DETAILS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* NEW: Evaluation Harshness */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Evaluation Harshness</label>
            <select
              className="border p-2 rounded"
              value={form.job_req_ai_evaluation_harshness}
              onChange={(e) =>
                setForm({ ...form, job_req_ai_evaluation_harshness: Number(e.target.value) })
              }
            >
              {HARSHNESS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Job Summary (350 chars) */}
          <div className="flex flex-col lg:col-span-4">
            <label className="text-xs text-gray-600 mb-1">
              Job Summary to be narrated by AI for Candidates (350 characters max)
            </label>
            <textarea
              className="border p-2 rounded"
              value={form.job_req_summary}
              minLength={20}
              maxLength={350}
              onChange={(e) => setForm({ ...form, job_req_summary: e.target.value })}
              placeholder="250-350 characters recommended. The AI will say this at the beginning of the interview..."
              rows={3}
            />
            <div className="text-xs text-gray-400 text-right">{(form.job_req_summary ?? "").length}/350</div>
          </div>

          <div className="flex flex-col lg:col-span-4">
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-600">Description</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={`px-2 py-0.5 text-xs rounded ${
                    descriptionViewMode === "edit"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  }`}
                  onClick={() => setDescriptionViewMode("edit")}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={`px-2 py-0.5 text-xs rounded ${
                    descriptionViewMode === "preview"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  }`}
                  onClick={() => setDescriptionViewMode("preview")}
                >
                  Preview HTML
                </button>
              </div>
            </div>
            {descriptionViewMode === "edit" ? (
              <>
                <textarea
                  className="border p-2 rounded"
                  value={form.job_req_description}
                  minLength={300}
                  maxLength={32768}
                  onChange={(e) => setForm({ ...form, job_req_description: e.target.value })}
                  placeholder="Job description (3,000-20,000 characters recommended)"
                  rows={8}
                />
                <div className="text-xs text-gray-400 text-right">{(form.job_req_description ?? "").length.toLocaleString()}/32,768</div>
              </>
            ) : (
              <div
                className="border p-3 rounded bg-white min-h-[200px] max-h-[400px] overflow-y-auto prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: form.job_req_description || "<em class='text-gray-400'>No description to preview</em>" }}
              />
            )}
          </div>

          {/* AI Assistant Button - Below Description */}
          <div className="lg:col-span-4 flex justify-end">
            <button
              type="button"
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 flex items-center gap-2"
              onClick={openAiModal}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
                <circle cx="7.5" cy="14.5" r="1.5"/>
                <circle cx="16.5" cy="14.5" r="1.5"/>
              </svg>
              AI Assistant - Generate Job Summary &amp; Description
            </button>
          </div>

          <div className="lg:col-span-4 flex gap-2">
            <button type="submit" className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700">
              {editingId ? "Update" : "Create"}
            </button>
            {editingId && (
              <button type="button" className="bg-gray-200 px-3 py-1 rounded" onClick={cancelEdit}>
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* AI Assistant Modal */}
        {aiModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-purple-700">AI Assistant - Generate Job Summary &amp; Description</h2>
                  <button
                    type="button"
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                    onClick={closeAiModal}
                  >
                    &times;
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Job Title */}
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">Job Title</label>
                    <input
                      type="text"
                      className="border p-2 rounded"
                      value={aiModalForm.job_title}
                      minLength={5}
                      maxLength={128}
                      onChange={(e) => setAiModalForm({ ...aiModalForm, job_title: e.target.value })}
                      placeholder="Job Title"
                    />
                    <div className="text-xs text-gray-400 text-right">{(aiModalForm.job_title ?? "").length}/128</div>
                  </div>

                  {/* Information About The Job */}
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">Information About The Job</label>
                    <textarea
                      className="border p-2 rounded"
                      value={aiModalForm.job_info}
                      minLength={15}
                      maxLength={32768}
                      onChange={(e) => setAiModalForm({ ...aiModalForm, job_info: e.target.value })}
                      placeholder="List whatever keywords or key skills you like to include in the job description. Example: 'Microsoft Excel, .net development, python, leadership skills', etc."
                      rows={5}
                    />
                    <div className="text-xs text-gray-400 text-right">{(aiModalForm.job_info ?? "").length.toLocaleString()}/32,768</div>
                  </div>

                  {/* Job Summary & Description Language */}
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">Job Summary &amp; Description Language</label>
                    <select
                      className="border p-2 rounded"
                      value={aiModalForm.language}
                      onChange={(e) => setAiModalForm({ ...aiModalForm, language: e.target.value })}
                    >
                      <option value="">Select Language</option>
                      {(aiModalLanguages || []).map((lang, idx) => (
                        <option key={idx} value={lang.interview_language_n8n_descr || lang}>
                          {lang.interview_language_n8n_descr || lang}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Job Description Format */}
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">Job Description Format</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="ai_format"
                          value="text"
                          checked={aiModalForm.format === "text"}
                          onChange={(e) => setAiModalForm({ ...aiModalForm, format: e.target.value })}
                        />
                        <span>Text</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="ai_format"
                          value="html"
                          checked={aiModalForm.format === "html"}
                          onChange={(e) => setAiModalForm({ ...aiModalForm, format: e.target.value })}
                        />
                        <span>HTML</span>
                      </label>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <div className="flex justify-end gap-2 pt-4">
                    <button
                      type="button"
                      className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
                      onClick={closeAiModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      onClick={handleAiGenerate}
                      disabled={aiGenerating}
                    >
                      {aiGenerating ? (
                        <>
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating...
                        </>
                      ) : (
                        "Generate Job Summary & Description"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            {/* Clear Filters button above table */}
            <div className="flex justify-end mb-2">
              <button
                type="button"
                className="px-3 py-1 rounded text-sm font-semibold border border-blue-500 text-blue-600 hover:bg-blue-50"
                title="Reset all table filters"
                onClick={clearAllFilters}
              >
                Clear Filters
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded shadow text-left text-[13px]">
                <thead>
                  <tr>
                    {headerCell("ID", "id")}
                    {headerCell("Title", "title")}
                    {headerCell("Area of Interest", "area")}
                    {headerCell("Location", "location")}
                    {headerCell("AI Interview Difficulty", "difficulty")}
                    {headerCell("AI Interview Details", "details")}
                    {headerCell("Evaluation Harshness", "harshness")}
                    {headerCell("Status", "status")}
                    {isSuper && headerCell("Organization", "org")}
                    <th className="p-2 border-b">Actions</th>
                    <th className="p-2 border-b text-center">Stats</th>
                  </tr>
                </thead>
                <tbody>
                  {(pageRows || []).map((j) => {
                    const id = j.job_req_id ?? j.id;
                    const statusVal = Number(j.job_req_status ?? j.status);
                    const isNonActive = NON_ACTIVE_STATUSES.has(statusVal);
                    const orgCell =
                      orgNameById.get(j.organization_id) ??
                      (j.organization_id != null ? `#${j.organization_id}` : "-");

                    const dropdownOptions = STATUS_OPTIONS.filter((o) => o.value !== statusVal);

                    // Row styling for non-active jobs (On Hold, Cancelled, Closed)
                    const rowStyle = isNonActive
                      ? { backgroundColor: "rgb(229, 229, 229)", color: "rgb(107, 114, 128)" }
                      : undefined;

                    return (
                      <tr key={id} className="hover:bg-gray-50" style={rowStyle}>
                        <td className="p-2 border-b">{id}</td>
                        <td
                          className="p-2 border-b text-blue-700 underline cursor-pointer"
                          onClick={() => gotoApplicants(id)}
                          title="View applicants"
                        >
                          {j.job_req_title ?? j.title}
                        </td>
                        <td className="p-2 border-b">{areaOfInterestText(j)}</td>
                        <td className="p-2 border-b">{locationText(j)}</td>

                        {/* AI Interview Difficulty */}
                        <td className="p-2 border-b">
                          {difficultyLabel(j.job_req_ai_interview_difficulty_level)}
                        </td>

                        {/* AI Interview Details */}
                        <td className="p-2 border-b">
                          {detailsLabel(j.job_req_ai_interview_details_level)}
                        </td>

                        {/* Evaluation Harshness */}
                        <td className="p-2 border-b">
                          {harshnessLabel(j.job_req_ai_evaluation_harshness)}
                        </td>

                        {/* Status - moved to before Actions */}
                        <td className="p-2 border-b">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-800 text-xs">
                              {statusLabel(statusVal)}
                            </span>
                            <select
                              className="border rounded px-1 py-0.5 text-xs bg-white"
                              value="__change__"
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v !== "__change__") changeRowStatus(id, Number(v));
                                e.target.value = "__change__";
                              }}
                          >
                            <option value="__change__" disabled>
                              Change…
                            </option>
                            {dropdownOptions.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>

                      {isSuper && <td className="p-2 border-b">{orgCell}</td>}

                      {/* Actions - last column */}
                      <td className="p-2 border-b">
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {/* Edit */}
                          <button
                            type="button"
                            title="Edit"
                            aria-label="Edit"
                            className="p-1 rounded hover:bg-blue-50 text-blue-700"
                            onClick={() => startEdit(j)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M17.414 2.586a2 2 0 0 0-2.828 0L6 11.172V14h2.828l8.586-8.586a2 2 0 0 0 0-2.828z" />
                              <path d="M5 15h10v2H5z" />
                            </svg>
                          </button>

                          {/* Hide */}
                          <button
                            type="button"
                            title="Hide"
                            aria-label="Hide"
                            className="p-1 rounded hover:bg-yellow-50 text-amber-600"
                            onClick={() => hideJob(id)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3.53 2.47 2.47 3.53 6.2 7.27A11.5 11.5 0 0 0 1.5 12c1.73 3.64 5.5 7 10.5 7 2.05 0 3.97-.57 5.6-1.55l3.87 3.88 1.06-1.06L3.53 2.47zM12 7c1.12 0 2.16.33 3.03.9l-1.5 1.5A3.5 3.5 0 0 0 8.6 14.33l-1.5 1.5A5.5 5.5 0 0 1 12 7zm10.5 5c-.73 1.54-1.9 3-3.37 4.2l-1.43-1.43A9.48 9.48 0 0 0 20.5 12c-1.73-3.64-5.5-7-10.5-7z"/>
                            </svg>
                          </button>

                          {/* Delete */}
                          <button
                            type="button"
                            title="Delete"
                            aria-label="Delete"
                            className="p-1 rounded hover:bg-red-50 text-red-700"
                            onClick={() => removeWithConfirm(id)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M6 8h2v8H6zM12 8h2v8h-2z" />
                              <path d="M4 6h12v2H4zM7 2h6v2H7zM5 6l1 12h8l1-12H5z" />
                            </svg>
                          </button>
                        </div>
                      </td>

                      {/* Stats column with info tooltip */}
                      <td className="p-2 border-b text-center">
                        <ApplicantStatsTooltip job={j} />
                      </td>
                    </tr>
                  );
                })}
                {(!sortedRows || sortedRows.length === 0) && (
                  <tr>
                    <td colSpan={isSuper ? 11 : 10} className="p-3 text-gray-500">No jobs</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination controls */}
            <div className="flex items-center justify-between mt-3">
              <div className="text-sm text-gray-600">
                Showing <b>{total === 0 ? 0 : startIdx + 1}</b>–<b>{Math.min(startIdx + pageSize, total)}</b> of{" "}
                <b>{total}</b>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="text-sm">Page {currentPage} / {totalPages}</span>
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
                <select
                  className="ml-3 border p-1 rounded text-sm"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
                </select>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ==================== Filter Components ====================

function FilterIcon({ active }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`w-4 h-4 ${active ? "text-blue-600" : "text-gray-400"}`}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M3 5h18v2l-7 8v4l-4 2v-6L3 7V5z" />
    </svg>
  );
}

function FilterDropdown({ open, onClose, anchorRef, values, selectedSet, onChange }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const anchorEl = anchorRef?.current;
    if (anchorEl) {
      const r = anchorEl.getBoundingClientRect();
      // Position the dropdown so its top-left corner is at the filter icon's bottom-left
      const top = r.bottom + window.scrollY + 2;
      const left = r.left + window.scrollX;
      setPos({ top, left });
    }

    const onDoc = (e) => {
      const p = panelRef.current;
      const a = anchorRef?.current;
      if (!p) return;
      if (p.contains(e.target)) return;
      if (a && a.contains(e.target)) return;
      onClose?.();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const list = Array.isArray(values) ? values : [];
  const selected = selectedSet instanceof Set ? selectedSet : new Set();

  const toggle = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange?.(next);
  };

  const clearAll = () => onChange?.(new Set());
  const selectAll = () => onChange?.(new Set(list));

  return (
    <div
      ref={panelRef}
      className="fixed z-50 bg-white border rounded shadow-md p-2 w-64"
      style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
    >
      <div className="flex items-center justify-between mb-2">
        <button type="button" className="text-xs text-blue-600 hover:underline" onClick={selectAll}>
          Select all
        </button>
        <button type="button" className="text-xs text-gray-600 hover:underline" onClick={clearAll}>
          Clear
        </button>
      </div>

      <div className="max-h-64 overflow-auto">
        {list.length === 0 ? (
          <div className="text-xs text-gray-500 p-2">No values</div>
        ) : (
          list.map((val) => (
            <label key={String(val)} className="flex items-center gap-2 text-sm px-1 py-1">
              <input
                type="checkbox"
                checked={selected.has(val)}
                onChange={() => toggle(val)}
              />
              <span className="truncate">{String(val)}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

// ==================== Applicant Stats Tooltip Component ====================

function ApplicantStatsTooltip({ job }) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState({ top: 0, left: 0 });
  const iconRef = React.useRef(null);

  const applicantsTotal = Number(job.applicants_total ?? 0);
  const applicantsInterviewed = Number(job.applicants_interviewed ?? 0);
  const applicantsNotConsidered = Number(job.applicants_not_considered ?? 0);
  const applicantsInProgress = Number(job.applicants_in_progress ?? 0);
  const applicantsAiRecommended = Number(job.applicants_ai_recommended ?? 0);
  const applicantsAiRecommendedPercentage = applicantsInterviewed > 0
    ? Math.round((applicantsAiRecommended / applicantsInterviewed) * 100)
    : 0;

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipWidth = 380; // Approximate tooltip width
      
      // Position tooltip to the left of the icon to avoid right-edge cutoff
      let left = rect.left - tooltipWidth - 10;
      
      // If tooltip would go off left edge, position it to fit within viewport
      if (left < 10) {
        left = 10;
      }
      
      setTooltipPos({
        top: rect.top + window.scrollY - 20,
        left: left,
      });
    }
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div className="relative inline-block">
      <span
        ref={iconRef}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 cursor-help text-xs font-semibold"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        i
      </span>

      {showTooltip && (
        <div
          className="fixed z-50 bg-gray-900 text-white text-xs rounded shadow-lg p-3"
          style={{ top: `${tooltipPos.top}px`, left: `${tooltipPos.left}px`, minWidth: '360px' }}
        >
          <table className="w-full">
            <tbody>
              <tr>
                <td className="pr-4 py-0.5 text-gray-300 text-left">Total Applicants:</td>
                <td className="py-0.5 font-semibold text-left">{applicantsTotal}</td>
              </tr>
              <tr>
                <td className="pr-4 py-0.5 text-gray-300 text-left">Interviewed:</td>
                <td className="py-0.5 font-semibold text-left">{applicantsInterviewed}</td>
              </tr>
              <tr>
                <td className="pr-4 py-0.5 text-gray-300 text-left">Not Considered:</td>
                <td className="py-0.5 font-semibold text-left">{applicantsNotConsidered}</td>
              </tr>
              <tr>
                <td className="pr-4 py-0.5 text-gray-300 text-left">In Progress:</td>
                <td className="py-0.5 font-semibold text-left">{applicantsInProgress}</td>
              </tr>
              <tr>
                <td className="pr-4 py-0.5 text-gray-300 text-left">AI Recommended from the ones interviewed:</td>
                <td className="py-0.5 font-semibold text-left">
                  {applicantsAiRecommended} ({applicantsAiRecommendedPercentage}%)
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}